<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { NotificationDto } from '@/types'

import NotificationAttachments from '@/components/notifications/NotificationAttachments.vue'
import * as notificationService from '@/services/notificationService'

definePage({
  meta: {
    requiresAuth: true,
  },
})

// Notification composable
const {
  sortedNotifications,
  isLoading: notificationLoading,
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  archiveNotification,
  unarchiveNotification,
  initialize,
  error,
  fetchUnreadCount,
} = useNotifications()
const { d, t } = useI18n()

// Mobile responsiveness
const isMobile = ref(false)
const showMobileDetail = ref(false)
const MOBILE_BREAKPOINT = 768

const updateMobileState = () => {
  isMobile.value = window.innerWidth < MOBILE_BREAKPOINT
  // Reset mobile detail view when switching to desktop
  if (!isMobile.value) {
    showMobileDetail.value = false
  }
}

// Local state
const filter = ref<'all' | 'unread' | 'read' | 'archived'>('all')
const counts = ref({ all: 0, unread: 0, read: 0, archived: 0 })
const filterOptions = computed(() => {
  return [
    { label: `${t('pages.user.notifications.filters.all')} (${counts.value.all})`, value: 'all' },
    {
      label: `${t('pages.user.notifications.filters.unread')} (${counts.value.unread})`,
      value: 'unread',
    },
    {
      label: `${t('pages.user.notifications.filters.read')} (${counts.value.read})`,
      value: 'read',
    },
    {
      label: `${t('pages.user.notifications.filters.archived')} (${counts.value.archived})`,
      value: 'archived',
    },
  ]
})
const isMarkingAllRead = ref(false)
const isRefreshing = ref(false)
const markingRead = ref<number | null>(null)
const archivingId = ref<number | null>(null)
const selectedNotification = ref<NotificationDto | null>(null)
const collapsedGroups = ref<Set<string>>(new Set())
const searchQuery = ref('')

// Computed properties
const filteredNotifications = computed(() => {
  if (!searchQuery.value.trim()) {
    return sortedNotifications.value
  }

  const query = searchQuery.value.toLowerCase()
  return sortedNotifications.value.filter((notification) => {
    const title = notification.title.toLowerCase()
    const message = notification.message.toLowerCase()
    const senderName = notification.senderName?.toLowerCase() || ''
    const senderEmail = notification.senderEmail?.toLowerCase() || ''

    return (
      title.includes(query) ||
      message.includes(query) ||
      senderName.includes(query) ||
      senderEmail.includes(query)
    )
  })
})

const isLoading = computed(
  () => notificationLoading.value && sortedNotifications.value.length === 0,
)
const hasUnread = computed(() => sortedNotifications.value.some((n) => !n.isRead))

// Empty state message based on filter
const emptyMessage = computed(() => {
  if (filter.value === 'all') {
    return t('pages.user.notifications.empty.all')
  } else if (filter.value === 'unread') {
    return t('pages.user.notifications.empty.unread')
  } else if (filter.value === 'read') {
    return t('pages.user.notifications.empty.read')
  } else if (filter.value === 'archived') {
    return t('pages.user.notifications.empty.archived')
  }
  return t('pages.user.notifications.empty.default')
})

// Truncate message for list view
const truncateMessage = (message: string, maxLength = 80) => {
  // Strip HTML tags first for preview
  const strippedMessage = message.replace(/<[^>]*>/g, '')
  if (strippedMessage.length <= maxLength) {
    return strippedMessage
  }
  return `${strippedMessage.substring(0, maxLength)}...`
}

// Date grouping functions
const getDateGroup = (date: Date): string => {
  const now = new Date()
  const notificationDate = new Date(date)

  // Reset time to midnight for accurate day comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const compareDate = new Date(
    notificationDate.getFullYear(),
    notificationDate.getMonth(),
    notificationDate.getDate(),
  )

  const diffTime = today.getTime() - compareDate.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return 'today'
  } else if (diffDays === 1) {
    return 'yesterday'
  } else if (diffDays <= 7) {
    return 'thisWeek'
  } else if (diffDays <= 14) {
    return 'lastWeek'
  } else if (diffDays <= 30) {
    return 'lastMonth'
  } else {
    return 'older'
  }
}

const groupedNotifications = computed(() => {
  const groups: Record<string, NotificationDto[]> = {
    today: [],
    yesterday: [],
    thisWeek: [],
    lastWeek: [],
    lastMonth: [],
    older: [],
  }

  filteredNotifications.value.forEach((notification) => {
    const group = getDateGroup(new Date(notification.createdAt))
    const bucket = groups[group]
    if (bucket) {
      bucket.push(notification)
    }
  })

  // Return only non-empty groups in order
  return Object.entries(groups)
    .filter(([, notifications]) => notifications.length > 0)
    .map(([group, notifications]) => ({ group, notifications }))
})

const getGroupLabel = (group: string): string => {
  const labels: Record<string, string> = {
    today: t('pages.user.notifications.groups.today'),
    yesterday: t('pages.user.notifications.groups.yesterday'),
    thisWeek: t('pages.user.notifications.groups.thisWeek'),
    lastWeek: t('pages.user.notifications.groups.lastWeek'),
    lastMonth: t('pages.user.notifications.groups.lastMonth'),
    older: t('pages.user.notifications.groups.older'),
  }
  return labels[group] || group
}

const toggleGroup = (group: string) => {
  if (collapsedGroups.value.has(group)) {
    collapsedGroups.value.delete(group)
  } else {
    collapsedGroups.value.add(group)
  }
}

const isGroupCollapsed = (group: string): boolean => {
  return collapsedGroups.value.has(group)
}

// Methods
const fetchCounts = async () => {
  try {
    counts.value = await notificationService.fetchNotificationCounts()
  } catch (err) {
    console.error('Error fetching notification counts:', err)
  }
}

const markAsRead = async (id: number) => {
  markingRead.value = id
  try {
    await markNotificationAsRead(id)
    // Refresh counts after marking as read
    await Promise.all([
      fetchCounts(),
      fetchUnreadCount(), // Update global unread count for AppMenu badge
    ])
  } finally {
    markingRead.value = null
  }
}

// Mobile back navigation
const goBackToList = () => {
  // Mark current notification as read before going back
  if (selectedNotification.value && !selectedNotification.value.isRead) {
    markAsRead(selectedNotification.value.id)
  }
  showMobileDetail.value = false
}

// Select notification
const selectNotification = async (notification: NotificationDto) => {
  // Mark previous notification as read when switching to a new one
  if (selectedNotification.value && !selectedNotification.value.isRead) {
    await markAsRead(selectedNotification.value.id)
  }

  selectedNotification.value = notification

  // On mobile, show the detail view
  if (isMobile.value) {
    showMobileDetail.value = true
  }
}
const markAllAsRead = async () => {
  if (isMarkingAllRead.value) {
    return
  }

  isMarkingAllRead.value = true
  try {
    await markAllNotificationsAsRead()
    // Refresh counts after marking all as read
    await Promise.all([
      fetchCounts(),
      fetchUnreadCount(), // Update global unread count for AppMenu badge
    ])
  } finally {
    isMarkingAllRead.value = false
  }
}
const refreshNotifications = async () => {
  if (isRefreshing.value) {
    return
  }

  isRefreshing.value = true
  try {
    await Promise.all([fetchNotifications(filter.value), fetchCounts()])
  } finally {
    isRefreshing.value = false
  }
}

const archiveNotificationById = async (id: number) => {
  archivingId.value = id
  try {
    await archiveNotification(id)
    // If this was the selected notification, clear selection
    if (selectedNotification.value?.id === id) {
      selectedNotification.value = null
    }
    // Refresh counts after archiving
    await fetchCounts()
  } finally {
    archivingId.value = null
  }
}

const unarchiveNotificationById = async (id: number) => {
  archivingId.value = id
  try {
    await unarchiveNotification(id)
    // Refresh counts after unarchiving
    await fetchCounts()
  } finally {
    archivingId.value = null
  }
}

// Keyboard navigation
const handleKeydown = (event: KeyboardEvent) => {
  if (sortedNotifications.value.length === 0) {
    return
  }

  const currentIndex = selectedNotification.value
    ? sortedNotifications.value.findIndex((n) => n.id === selectedNotification.value?.id)
    : -1

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      if (currentIndex < sortedNotifications.value.length - 1) {
        const nextNotification = sortedNotifications.value[currentIndex + 1]
        if (nextNotification) {
          selectNotification(nextNotification)
        }
      }
      break
    case 'ArrowUp':
      event.preventDefault()
      if (currentIndex > 0) {
        const previousNotification = sortedNotifications.value[currentIndex - 1]
        if (previousNotification) {
          selectNotification(previousNotification)
        }
      }
      break
    case 'Enter':
    case ' ':
      event.preventDefault()
      if (selectedNotification.value && !selectedNotification.value.isRead) {
        markAsRead(selectedNotification.value.id)
      }
      break
  }
}
// Watch for filter changes to fetch appropriate data
watch(filter, () => {
  // Mark current notification as read before changing filters
  if (selectedNotification.value && !selectedNotification.value.isRead) {
    markAsRead(selectedNotification.value.id)
  }

  // Fetch notifications for the current filter
  fetchNotifications(filter.value)
  selectedNotification.value = null // Clear selection when changing filters
})

// Lifecycle hooks
onMounted(async () => {
  // Initialize mobile state and add resize listener
  updateMobileState()
  window.addEventListener('resize', updateMobileState)

  // Initialize notification store if needed
  initialize()

  // Fetch counts and initial notifications for 'all' filter
  await Promise.all([fetchCounts(), fetchNotifications('all')])

  // Add keyboard listeners
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  // Mark currently selected notification as read before leaving
  if (selectedNotification.value && !selectedNotification.value.isRead) {
    markAsRead(selectedNotification.value.id)
  }

  // Remove event listeners
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', updateMobileState)
})

// Watch for notifications loading to auto-select first unread
watch(
  [sortedNotifications, notificationLoading],
  ([notifs, loading]) => {
    if (!loading && notifs.length > 0 && !selectedNotification.value) {
      // Auto-select first unread notification, or first notification if none unread
      const firstUnread = notifs.find((n) => !n.isRead)
      if (firstUnread) {
        // Just set the selection without marking as read (initial selection)
        selectedNotification.value = firstUnread
      } else {
        const firstNotification = notifs[0]
        if (firstNotification) {
          selectedNotification.value = firstNotification
        }
      }
    }
  },
  { immediate: true },
)

// Note: Additional filter change handling is done in the earlier watch above
</script>

<template>
  <div class="notifications-page max-w-full">
    <!-- Mobile Detail View (shown when a notification is selected on mobile) -->
    <div v-if="isMobile && showMobileDetail && selectedNotification" class="mobile-detail-view">
      <!-- Mobile Header with Back Button -->
      <div class="mb-4 flex items-center gap-2">
        <UButton
          icon="i-lucide-arrow-left"
          :label="t('pages.user.notifications.actions.backToList')"
          color="neutral"
          @click="goBackToList"
          variant="ghost"
        />
      </div>

      <!-- Mobile Detail Content -->
      <div class="mycard">
        <!-- Content Header -->
        <div
          class="border-b border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-700 dark:bg-neutral-800"
        >
          <div class="mb-2 flex items-start justify-between gap-4">
            <h2 class="flex-1 text-lg font-semibold">
              {{ selectedNotification.title }}
            </h2>
            <div class="flex items-center gap-2">
              <UButton
                v-if="!selectedNotification.isRead && filter !== 'archived'"
                icon="i-lucide-check"
                size="sm"
                :aria-label="t('pages.user.notifications.actions.markAsRead')"
                :loading="markingRead === selectedNotification.id"
                :disabled="markingRead !== null || archivingId !== null"
                @click="markAsRead(selectedNotification.id)"
                variant="ghost"
              />
              <UButton
                v-if="filter === 'archived'"
                v-app-tooltip="t('pages.user.notifications.actions.unarchive')"
                icon="i-lucide-inbox"
                size="sm"
                :aria-label="t('pages.user.notifications.actions.unarchive')"
                :loading="archivingId === selectedNotification.id"
                :disabled="archivingId !== null"
                @click="unarchiveNotificationById(selectedNotification.id)"
                variant="ghost"
              />
              <UButton
                v-else
                v-app-tooltip="t('pages.user.notifications.actions.archive')"
                icon="i-lucide-folder"
                size="sm"
                :aria-label="t('pages.user.notifications.actions.archive')"
                :loading="archivingId === selectedNotification.id"
                :disabled="archivingId !== null || markingRead !== null"
                @click="archiveNotificationById(selectedNotification.id)"
                variant="ghost"
              />
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            <span class="flex items-center gap-1">
              <UIcon name="i-lucide-calendar" />
              {{ d(selectedNotification.createdAt, 'datetime') }}
            </span>
            <span
              v-if="selectedNotification.senderName || selectedNotification.senderEmail"
              class="flex items-center gap-1"
            >
              <UIcon name="i-lucide-user" />
              {{ t('pages.user.notifications.details.from') }}
              {{ selectedNotification.senderName || selectedNotification.senderEmail }}
            </span>
            <UBadge
              v-if="!selectedNotification.isRead"
              :label="t('pages.user.notifications.badges.unreadStatus')"
              color="info"
            />
          </div>
          <div
            class="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400"
          >
            <span v-if="selectedNotification.emailSent" class="flex items-center gap-1">
              <UIcon name="i-lucide-mail" />
              {{ t('pages.user.notifications.details.emailSent') }}
              {{
                selectedNotification.emailSentAt
                  ? d(selectedNotification.emailSentAt, 'datetime')
                  : t('pages.user.notifications.details.emailSentYes')
              }}
            </span>
            <span
              v-else-if="selectedNotification.emailError"
              class="flex items-center gap-1 text-red-700 dark:text-red-400"
            >
              <UIcon name="i-lucide-circle-x" />
              {{ t('pages.user.notifications.details.emailFailed') }}
            </span>
            <span v-else class="flex items-center gap-1">
              <UIcon name="i-lucide-clock" />
              {{ t('pages.user.notifications.details.emailPending') }}
            </span>
          </div>
          <div
            v-if="selectedNotification.emailError"
            class="mt-2 rounded bg-red-50 p-2 text-xs text-red-600 dark:bg-red-900/20 dark:text-red-400"
          >
            <UIcon name="i-lucide-triangle-alert" class="mr-1" />
            <strong>{{ t('pages.user.notifications.details.emailError') }}</strong>
            {{ selectedNotification.emailError }}
          </div>
        </div>

        <!-- Content Body -->
        <div class="p-4">
          <div
            class="notification-message-content notification-message-surface max-w-none"
            v-html="selectedNotification.message"
          />
          <NotificationAttachments :notification="selectedNotification" />
        </div>
      </div>
    </div>

    <!-- Main List View (shown on desktop, or on mobile when detail is not shown) -->
    <div v-else>
      <div class="flex items-center gap-3">
        <h1 class="text-2xl font-bold">
          {{ t('pages.user.notifications.title') }}
        </h1>
      </div>

      <!-- Mobile: Stacked layout for filters and actions -->
      <div class="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div class="flex items-center gap-2 overflow-x-auto">
          <AppSegmentedControl
            v-model="filter"
            :options="filterOptions"
            option-label="label"
            option-value="value"
            :allow-empty="false"
            class="flex-shrink-0"
          />
        </div>
        <div class="flex flex-wrap gap-2">
          <AppIconField class="flex-1 md:flex-none">
            <AppInputIcon name="i-lucide-search" />
            <UInput
              v-model="searchQuery"
              :placeholder="t('pages.user.notifications.search.placeholder')"
              class="w-full md:w-80"
            />
          </AppIconField>
          <UButton
            v-app-tooltip="t('pages.user.notifications.actions.refreshTooltip')"
            icon="i-lucide-refresh-cw"
            color="neutral"
            class="mb-1"
            :aria-label="t('pages.user.notifications.actions.refreshTooltip')"
            :disabled="isRefreshing"
            :loading="isRefreshing"
            @click="refreshNotifications"
            variant="outline"
          />
          <UButton
            v-app-tooltip="t('pages.user.notifications.actions.markAllAsReadTooltip')"
            icon="i-lucide-check"
            color="neutral"
            class="mb-1"
            :aria-label="t('pages.user.notifications.actions.markAllAsReadTooltip')"
            :disabled="!hasUnread || isMarkingAllRead"
            :loading="isMarkingAllRead"
            @click="markAllAsRead"
            variant="outline"
          />
        </div>
      </div>

      <div class="mycard notifications-container p-4">
        <!-- Loading state -->
        <div v-if="isLoading" class="flex justify-center py-6">
          <AppSpinner style="width: 50px; height: 50px" />
        </div>

        <!-- Error state -->
        <div
          v-else-if="error"
          class="rounded-md bg-red-50 p-4 text-red-700 dark:bg-red-900 dark:text-red-200"
        >
          <UIcon name="i-lucide-triangle-alert" class="mr-2" />
          <span>{{ error }}</span>
          <UButton
            :label="t('pages.user.notifications.actions.retry')"
            color="error"
            class="ml-2"
            @click="fetchNotifications(filter)"
            variant="ghost"
          />
        </div>

        <!-- Empty state -->
        <div
          v-else-if="filteredNotifications.length === 0"
          class="p-6 text-center text-gray-500 dark:text-gray-400"
        >
          <UIcon
            class="mb-2 text-3xl opacity-50"
            :name="searchQuery ? 'i-lucide-search' : 'i-lucide-bell'"
          />
          <p class="mb-1 font-medium">
            {{ t('pages.user.notifications.empty.title') }}
          </p>
          <p class="text-sm">
            {{
              searchQuery
                ? t('pages.user.notifications.search.noResults', { query: searchQuery })
                : emptyMessage
            }}
          </p>
          <UButton
            v-if="searchQuery"
            :label="t('pages.user.notifications.search.clearButton')"
            size="sm"
            class="mt-2"
            @click="searchQuery = ''"
            variant="ghost"
          />
        </div>

        <!-- Desktop: Email-client split layout -->
        <div v-else-if="!isMobile" class="notification-layout">
          <!-- Notification List Panel -->
          <section class="notification-list flex min-h-0 min-w-0 flex-col overflow-hidden">
            <div class="notification-scroll-region flex-1 overflow-y-auto">
              <!-- Date-grouped notifications -->
              <div
                v-for="{ group, notifications: groupNotifications } in groupedNotifications"
                :key="group"
                class="notification-group"
              >
                <!-- Group header -->
                <button
                  type="button"
                  class="group-header sticky top-0 z-10 flex w-full cursor-pointer items-center gap-2 border-b border-neutral-200 bg-neutral-50 px-3 py-2 text-left text-xs font-semibold tracking-wide text-gray-600 uppercase transition-colors hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-gray-400 dark:hover:bg-neutral-700"
                  :aria-expanded="!isGroupCollapsed(group)"
                  @click="toggleGroup(group)"
                >
                  <UIcon
                    class="transition-transform duration-200"
                    :name="
                      isGroupCollapsed(group) ? 'i-lucide-chevron-right' : 'i-lucide-chevron-down'
                    "
                  />
                  <span>{{ getGroupLabel(group) }} ({{ groupNotifications.length }})</span>
                </button>

                <!-- Notifications in this group -->
                <template v-if="!isGroupCollapsed(group)">
                  <div
                    v-for="notification in groupNotifications"
                    :key="notification.id"
                    class="notification-item cursor-pointer border-b border-neutral-200 p-3 transition-colors hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-800"
                    :class="{
                      'border-l-4 border-l-blue-500! bg-blue-50 dark:bg-blue-900/30':
                        !notification.isRead,
                      'bg-neutral-100 dark:bg-neutral-700':
                        selectedNotification?.id === notification.id,
                    }"
                    @click="selectNotification(notification)"
                  >
                    <div class="flex items-start gap-2">
                      <div class="min-w-0 flex-1">
                        <div class="mb-1 flex items-center gap-2">
                          <h3
                            class="truncate text-sm font-medium"
                            :class="{ 'font-semibold': !notification.isRead }"
                          >
                            {{ notification.title }}
                          </h3>
                        </div>
                        <p class="mb-1 line-clamp-2 text-xs text-gray-600 dark:text-gray-400">
                          {{ truncateMessage(notification.message, 100) }}
                        </p>
                        <div
                          class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400"
                        >
                          <span>{{ d(notification.createdAt, 'datetime') }}</span>
                          <span v-if="notification.senderName" class="ml-2 truncate">
                            {{ notification.senderName }}
                          </span>
                        </div>
                      </div>
                      <div class="flex items-center gap-1">
                        <UButton
                          v-if="!notification.isRead && filter !== 'archived'"
                          icon="i-lucide-check"
                          size="sm"
                          :aria-label="t('pages.user.notifications.actions.markAsRead')"
                          :loading="markingRead === notification.id"
                          :disabled="markingRead !== null || archivingId !== null"
                          @click.stop="markAsRead(notification.id)"
                          variant="ghost"
                        />
                        <UButton
                          v-if="filter === 'archived'"
                          v-app-tooltip="t('pages.user.notifications.actions.unarchiveTooltip')"
                          icon="i-lucide-inbox"
                          size="sm"
                          :aria-label="t('pages.user.notifications.actions.unarchive')"
                          :loading="archivingId === notification.id"
                          :disabled="archivingId !== null"
                          @click.stop="unarchiveNotificationById(notification.id)"
                          variant="ghost"
                        />
                        <UButton
                          v-else
                          v-app-tooltip="t('pages.user.notifications.actions.archiveTooltip')"
                          icon="i-lucide-folder"
                          size="sm"
                          :aria-label="t('pages.user.notifications.actions.archive')"
                          :loading="archivingId === notification.id"
                          :disabled="archivingId !== null || markingRead !== null"
                          @click.stop="archiveNotificationById(notification.id)"
                          variant="ghost"
                        />
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </section>

          <!-- Notification Content Panel -->
          <section class="notification-content flex min-h-0 min-w-0 flex-col overflow-hidden">
            <div v-if="selectedNotification" class="flex h-full flex-col">
              <!-- Content Header -->
              <div
                class="border-b border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-700 dark:bg-neutral-800"
              >
                <div class="mb-2 flex items-start justify-between gap-4">
                  <h2 class="flex-1 text-lg font-semibold">
                    {{ selectedNotification.title }}
                  </h2>
                  <div class="flex items-center gap-2">
                    <UButton
                      v-if="!selectedNotification.isRead && filter !== 'archived'"
                      icon="i-lucide-check"
                      size="sm"
                      :aria-label="t('pages.user.notifications.actions.markAsRead')"
                      :loading="markingRead === selectedNotification.id"
                      :disabled="markingRead !== null || archivingId !== null"
                      @click="markAsRead(selectedNotification.id)"
                      variant="ghost"
                    />
                    <UButton
                      v-if="filter === 'archived'"
                      v-app-tooltip="t('pages.user.notifications.actions.unarchive')"
                      icon="i-lucide-inbox"
                      size="sm"
                      :aria-label="t('pages.user.notifications.actions.unarchive')"
                      :loading="archivingId === selectedNotification.id"
                      :disabled="archivingId !== null"
                      @click="unarchiveNotificationById(selectedNotification.id)"
                      variant="ghost"
                    />
                    <UButton
                      v-else
                      v-app-tooltip="t('pages.user.notifications.actions.archive')"
                      icon="i-lucide-folder"
                      size="sm"
                      :aria-label="t('pages.user.notifications.actions.archive')"
                      :loading="archivingId === selectedNotification.id"
                      :disabled="archivingId !== null || markingRead !== null"
                      @click="archiveNotificationById(selectedNotification.id)"
                      variant="ghost"
                    />
                  </div>
                </div>
                <div
                  class="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400"
                >
                  <span class="flex items-center gap-1">
                    <UIcon name="i-lucide-calendar" />
                    {{ d(selectedNotification.createdAt, 'datetime') }}
                  </span>
                  <span
                    v-if="selectedNotification.senderName || selectedNotification.senderEmail"
                    class="flex items-center gap-1"
                  >
                    <UIcon name="i-lucide-user" />
                    {{ t('pages.user.notifications.details.from') }}
                    {{ selectedNotification.senderName || selectedNotification.senderEmail }}
                  </span>
                  <span v-if="selectedNotification.emailSent" class="flex items-center gap-1">
                    <UIcon name="i-lucide-mail" />
                    {{ t('pages.user.notifications.details.emailSent') }}
                    {{
                      selectedNotification.emailSentAt
                        ? d(selectedNotification.emailSentAt, 'datetime')
                        : t('pages.user.notifications.details.emailSentYes')
                    }}
                  </span>
                  <span
                    v-else-if="selectedNotification.emailError"
                    class="flex items-center gap-1 text-red-700 dark:text-red-400"
                  >
                    <UIcon name="i-lucide-circle-x" />
                    {{ t('pages.user.notifications.details.emailFailed') }}
                  </span>
                  <span v-else class="flex items-center gap-1">
                    <UIcon name="i-lucide-clock" />
                    {{ t('pages.user.notifications.details.emailPending') }}
                  </span>
                  <UBadge
                    v-if="!selectedNotification.isRead"
                    :label="t('pages.user.notifications.badges.unreadStatus')"
                    color="info"
                  />
                </div>
                <div
                  v-if="selectedNotification.emailError"
                  class="mt-2 rounded bg-red-50 p-2 text-xs text-red-600 dark:bg-red-900/20 dark:text-red-400"
                >
                  <UIcon name="i-lucide-triangle-alert" class="mr-1" />
                  <strong>{{ t('pages.user.notifications.details.emailError') }}</strong>
                  {{ selectedNotification.emailError }}
                </div>
              </div>

              <!-- Content Body -->
              <div class="notification-scroll-region flex-1 overflow-y-auto p-4">
                <div
                  class="notification-message-content notification-message-surface max-w-none"
                  v-html="selectedNotification.message"
                />
                <NotificationAttachments :notification="selectedNotification" />
              </div>
            </div>

            <!-- No Selection State -->
            <div
              v-else
              class="flex h-full items-center justify-center text-gray-500 dark:text-gray-400"
            >
              <div class="text-center">
                <UIcon name="i-lucide-mail" class="mb-4 text-4xl opacity-50" />
                <p class="mb-2 text-lg font-medium">
                  {{ t('pages.user.notifications.noSelection.title') }}
                </p>
                <p class="text-sm">
                  {{ t('pages.user.notifications.noSelection.subtitle') }}
                </p>
              </div>
            </div>
          </section>
        </div>

        <!-- Mobile: Stacked list layout -->
        <div v-else class="mobile-notification-list">
          <!-- Date-grouped notifications -->
          <div
            v-for="{ group, notifications: groupNotifications } in groupedNotifications"
            :key="group"
            class="notification-group"
          >
            <!-- Group header -->
            <button
              type="button"
              class="group-header sticky top-0 z-10 flex w-full cursor-pointer items-center gap-2 border-b border-neutral-200 bg-neutral-50 px-3 py-2 text-left text-xs font-semibold tracking-wide text-gray-600 uppercase transition-colors hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-gray-400 dark:hover:bg-neutral-700"
              :aria-expanded="!isGroupCollapsed(group)"
              @click="toggleGroup(group)"
            >
              <UIcon
                class="transition-transform duration-200"
                :name="isGroupCollapsed(group) ? 'i-lucide-chevron-right' : 'i-lucide-chevron-down'"
              />
              <span>{{ getGroupLabel(group) }} ({{ groupNotifications.length }})</span>
            </button>

            <!-- Notifications in this group -->
            <template v-if="!isGroupCollapsed(group)">
              <div
                v-for="notification in groupNotifications"
                :key="notification.id"
                class="notification-item cursor-pointer border-b border-neutral-200 p-3 transition-colors hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-800"
                :class="{
                  'border-l-4 border-l-blue-500! bg-blue-50 dark:bg-blue-900/30':
                    !notification.isRead,
                }"
                @click="selectNotification(notification)"
              >
                <div class="flex items-start gap-3">
                  <!-- Unread indicator -->
                  <div class="flex-shrink-0 pt-1">
                    <div
                      class="h-2 w-2 rounded-full"
                      :class="notification.isRead ? 'bg-transparent' : 'bg-blue-500'"
                    />
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="mb-1 flex items-start justify-between gap-2">
                      <h3
                        class="text-sm font-medium"
                        :class="{ 'font-semibold': !notification.isRead }"
                      >
                        {{ notification.title }}
                      </h3>
                      <UIcon name="i-lucide-chevron-right" class="flex-shrink-0 text-gray-400" />
                    </div>
                    <p class="mb-1 line-clamp-2 text-xs text-gray-600 dark:text-gray-400">
                      {{ truncateMessage(notification.message, 80) }}
                    </p>
                    <div
                      class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400"
                    >
                      <span>{{ d(notification.createdAt, 'datetime') }}</span>
                      <span v-if="notification.senderName" class="ml-2 truncate">
                        {{ notification.senderName }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Pagination Info (desktop only) -->
        <div
          v-if="sortedNotifications.length > 0 && !isMobile"
          class="flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-700"
        >
          <small class="text-gray-500 dark:text-gray-400">
            {{
              t(
                'pages.user.notifications.footer.showing',
                { filtered: filteredNotifications.length, total: sortedNotifications.length },
                sortedNotifications.length,
              )
            }}
          </small>
          <small class="text-gray-500 dark:text-gray-400">
            {{ t('pages.user.notifications.footer.keyboardHelp') }}
          </small>
        </div>

        <!-- Mobile: Simplified footer -->
        <div
          v-if="sortedNotifications.length > 0 && isMobile"
          class="border-t border-gray-200 pt-4 text-center dark:border-gray-700"
        >
          <small class="text-gray-500 dark:text-gray-400">
            {{
              t(
                'pages.user.notifications.footer.showing',
                { filtered: filteredNotifications.length, total: sortedNotifications.length },
                sortedNotifications.length,
              )
            }}
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.notification-layout {
  display: grid;
  grid-template-columns: minmax(18rem, 35%) minmax(0, 1fr);
  block-size: clamp(32rem, calc(100dvh - 20rem), 56rem);
  min-inline-size: 0;
  overflow: hidden;
  border: 1px solid var(--ui-border);
  border-radius: 0.75rem;
}

.notification-list {
  border-right: 1px solid var(--ui-border);
  background: var(--ui-bg);
}

.notification-scroll-region {
  min-block-size: 0;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

.notification-item {
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
}

.notification-item:hover {
  border-left-color: var(--ui-primary);
}

.notification-item.selected {
  border-left-color: var(--ui-primary);
  background-color: var(--ui-bg-elevated);
}

.notification-content {
  background: var(--ui-bg-muted);
}

/* Mobile notification list */
.mobile-notification-list {
  max-height: calc(100vh - 280px);
  overflow-y: auto;
}

.mobile-notification-list .notification-item {
  border-left: none;
}

.mobile-notification-list .notification-item:active {
  background-color: var(--ui-bg-elevated);
}

/* Mobile detail view */
.mobile-detail-view {
  animation: slideInRight 0.2s ease-out;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.notification-message-content {
  color: var(--notification-message-text);
  font-size: 0.9rem;
  line-height: 1.55;
  overflow-wrap: anywhere;
}

.notification-message-surface {
  --notification-message-text: #0f172a;
  --notification-message-muted: #475569;
  --notification-message-border: #cbd5e1;
  --notification-message-subtle: #f8fafc;

  color-scheme: only light;
  color: var(--notification-message-text);
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: clamp(0.875rem, 2vw, 1.25rem);
}

:deep(.notification-message-content > *) {
  margin: 0 !important;
}

:deep(.notification-message-content > * + *) {
  margin-top: 0.65rem !important;
}

:deep(.notification-message-content .email-body) {
  display: grid;
  gap: 0.95rem;
}

/* Fallback when notification HTML classes are stripped but div structure remains */
:deep(.notification-message-content > div:last-child) {
  display: grid;
  gap: 0.95rem;
}

:deep(.notification-message-content .greeting) {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--notification-message-text);
}

:deep(.notification-message-content > div:last-child > div:first-child) {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--notification-message-text);
}

:deep(.notification-message-content .content-section) {
  display: grid;
  gap: 0.9rem;
}

:deep(.notification-message-content > div:last-child > div:last-child) {
  display: grid;
  gap: 0.9rem;
}

:deep(.notification-message-content .content-section > :first-child) {
  margin-top: 0;
}

:deep(.notification-message-content > div:last-child > div:last-child > :first-child) {
  margin-top: 0 !important;
}

:deep(.notification-message-content .info-table) {
  display: grid;
  gap: 0.35rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--notification-message-border);
  border-radius: 0.5rem;
  background: var(--notification-message-subtle);
}

:deep(.notification-message-content > div:last-child > div:last-child > div) {
  display: grid;
  gap: 0.35rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--notification-message-border);
  border-radius: 0.5rem;
  background: var(--notification-message-subtle);
}

:deep(.notification-message-content .info-table p) {
  margin: 0 !important;
  line-height: 1.35;
}

:deep(.notification-message-content > div:last-child > div:last-child > div > p) {
  margin: 0 !important;
  line-height: 1.35;
}

:deep(.notification-message-content .info-table p + p) {
  padding-top: 0.32rem;
  border-top: 1px solid var(--notification-message-border);
}

:deep(.notification-message-content > div:last-child > div:last-child > div > p + p) {
  padding-top: 0.32rem;
  border-top: 1px solid var(--notification-message-border);
}

:deep(.notification-message-content .info-table img) {
  margin-top: 0.35rem;
}

:deep(.notification-message-content > div:last-child > div:last-child > div img) {
  margin-top: 0.35rem;
}

:deep(.notification-message-content h1),
:deep(.notification-message-content h2),
:deep(.notification-message-content h3),
:deep(.notification-message-content h4),
:deep(.notification-message-content h5),
:deep(.notification-message-content h6) {
  margin: 0 !important;
  font-weight: 600;
  line-height: 1.25;
  color: var(--notification-message-text);
}

:deep(.notification-message-content > h1) {
  font-size: 1.2rem;
  font-weight: 650;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

:deep(.notification-message-content > h1 + h1) {
  margin-top: 0.15rem !important;
}

:deep(.notification-message-content > h1 + div),
:deep(.notification-message-content > h1 + p) {
  margin-top: 0.55rem !important;
}

:deep(.notification-message-content p) {
  margin: 0 !important;
}

:deep(.notification-message-content div) {
  line-height: 1.55;
}

:deep(.notification-message-content ul),
:deep(.notification-message-content ol) {
  margin: 1em 0;
  padding-left: 1.5em;
}

:deep(.notification-message-content li) {
  margin: 0.5em 0;
}

:deep(.notification-message-content blockquote) {
  border-left: 4px solid var(--ui-primary);
  padding-left: 1em;
  margin: 1em 0;
  font-style: italic;
  color: var(--notification-message-muted);
}

:deep(.notification-message-content strong) {
  font-weight: 700;
}

:deep(.notification-message-content .info-table p > strong:first-child),
:deep(
  .notification-message-content > div:last-child > div:last-child > div > p > strong:first-child
) {
  color: var(--notification-message-text);
  display: inline-block;
  min-width: 10rem;
  margin-right: 0.25rem;
  vertical-align: top;
}

:deep(.notification-message-content code) {
  background: var(--notification-message-subtle);
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-size: 0.9em;
}

:deep(.notification-message-content pre) {
  background: var(--notification-message-subtle);
  padding: 1em;
  border-radius: 6px;
  overflow-x: auto;
  margin: 1em 0;
}

:deep(.notification-message-content img) {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
  margin: 1em 0;
}

:deep(.notification-message-content a) {
  color: #0369a1;
  text-decoration: underline;
}

:deep(.notification-message-content a:hover) {
  text-decoration: none;
}

:deep(.notification-message-content table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1em 0;
  font-size: 0.95em;
}

:deep(.notification-message-content th),
:deep(.notification-message-content td) {
  border: 1px solid var(--notification-message-border);
  padding: 0.5em;
  text-align: left;
  vertical-align: top;
}

:deep(.notification-message-content th) {
  background: var(--notification-message-subtle);
  font-weight: 600;
}

.single-column-layout .notification-card {
  transition: all 0.2s ease;
}

.single-column-layout .notification-card:hover {
  box-shadow: var(--card-shadow);
}

/* Date group headers */
.group-header {
  backdrop-filter: blur(8px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .notifications-page {
    padding: 0;
  }

  .mobile-notification-list {
    max-height: calc(100vh - 320px);
  }

  .group-header {
    padding: 0.5rem 0.75rem;
  }

  :deep(.notification-message-content > h1) {
    font-size: 1.05rem;
  }

  :deep(.notification-message-content .info-table p > strong:first-child),
  :deep(
    .notification-message-content > div:last-child > div:last-child > div > p > strong:first-child
  ) {
    display: block;
    min-width: 0;
    margin-right: 0;
    margin-bottom: 0.15rem;
  }
}

@media (min-width: 1024px) {
  :deep(.notification-message-content .info-table p > strong:first-child),
  :deep(
    .notification-message-content > div:last-child > div:last-child > div > p > strong:first-child
  ) {
    min-width: 11.25rem;
  }
}
</style>
